import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, Calendar, Clock, User, CheckCircle2, ClipboardList } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session storage key to track if alert was shown this session
const ALERT_SHOWN_KEY = 'myhelp_task_alert_shown';

const TaskDueAlert = () => {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [dueToday, setDueToday] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(false);

  // Check if alert was already shown this session
  const wasAlertShown = () => {
    return sessionStorage.getItem(ALERT_SHOWN_KEY) === 'true';
  };

  // Mark alert as shown for this session
  const markAlertShown = () => {
    sessionStorage.setItem(ALERT_SHOWN_KEY, 'true');
  };

  // Fetch tasks due today and overdue
  const fetchTaskAlerts = useCallback(async () => {
    // Skip if already shown this session
    if (wasAlertShown()) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/tasks/login-alert`);
      const { due_today, overdue: overdueData, has_alerts } = response.data;
      
      if (has_alerts) {
        setDueToday(due_today || []);
        setOverdue(overdueData || []);
        setShowDialog(true);
      }
    } catch (error) {
      // Silently fail - don't block user experience
      console.debug('Failed to check task alerts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch alerts on component mount (after login)
  useEffect(() => {
    // Small delay to ensure auth is fully established
    const timer = setTimeout(() => {
      fetchTaskAlerts();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [fetchTaskAlerts]);

  // Handle acknowledgement
  const handleAcknowledge = () => {
    markAlertShown();
    setShowDialog(false);
    // Navigate to dashboard (tasks are shown as a widget there)
    navigate('/dashboard');
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = parseISO(dateStr);
      return format(date, 'MMM d, yyyy');
    } catch {
      return dateStr.split('T')[0];
    }
  };

  // Format priority badge
  const getPriorityBadge = (priority) => {
    const colors = {
      urgent: 'bg-red-500 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-white',
      low: 'bg-gray-400 text-white'
    };
    return colors[priority] || colors.medium;
  };

  if (!showDialog || (dueToday.length === 0 && overdue.length === 0)) {
    return null;
  }

  return (
    <Dialog open={showDialog} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col" 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton={true}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            Task Reminder
          </DialogTitle>
          <DialogDescription>
            You have tasks that require your attention.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
          {/* Overdue Tasks */}
          {overdue.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Overdue Tasks ({overdue.length})
              </h3>
              <div className="space-y-2">
                {overdue.map((task) => (
                  <div 
                    key={task.id} 
                    className="p-3 bg-red-50 border border-red-200 rounded-lg"
                    data-testid={`overdue-task-${task.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{task.title}</p>
                        {task.participant_name && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <User className="w-3 h-3" />
                            {task.participant_name}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={getPriorityBadge(task.priority)}>
                          {task.priority || 'medium'}
                        </Badge>
                        <span className="text-xs text-red-600 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(task.due_date)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Due Today Tasks */}
          {dueToday.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold text-amber-600 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Due Today ({dueToday.length})
              </h3>
              <div className="space-y-2">
                {dueToday.map((task) => (
                  <div 
                    key={task.id} 
                    className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
                    data-testid={`due-today-task-${task.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{task.title}</p>
                        {task.participant_name && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <User className="w-3 h-3" />
                            {task.participant_name}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={getPriorityBadge(task.priority)}>
                          {task.priority || 'medium'}
                        </Badge>
                        <span className="text-xs text-amber-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Due Today
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button 
            onClick={handleAcknowledge}
            className="w-full bg-primary hover:bg-primary/90"
            data-testid="acknowledge-tasks-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Acknowledge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDueAlert;
